import React from "react";
import { Sliders, VolumeX, Zap, Sparkles, Globe } from "lucide-react";

export default function AdvancedConfigTab({
  selectedAgent,
  handleInputChange,
}) {
  return (
    <div className="tab-panel-content">
      {/* SECTION 1: Welcome Delay & Greeting Playback Parameters */}
      <div className="config-section-simple" style={{ marginBottom: 30 }}>
        <div className="config-group-title" style={{ display: "flex", alignItems: "center", gap: "8px", borderBottom: "1px solid var(--gray-100, #f1f5f9)", paddingBottom: "10px", marginBottom: "18px" }}>
          <Zap size={16} style={{ color: "var(--primary, #1e53b8)" }} />
          <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--slate-900)" }}>Synthesis Latency & Breathing Room</span>
        </div>

        {/* Maximum Silence Timeout Slider */}
        <div className="slider-group" style={{ marginBottom: 12 }}>
          <div className="slider-header">
            <span className="field-label">
              Maximum Silence Timeout <span style={{ color: "#94a3b8", fontWeight: "normal", fontSize: "12px", marginLeft: "4px" }}>(Optional)</span>
            </span>
            <span className="slider-val">{selectedAgent?.timeout ?? 60} sec</span>
          </div>
          <input
            type="range"
            min={5}
            max={120}
            step={5}
            className="custom-range-slider"
            value={selectedAgent?.timeout ?? 60}
            onChange={(e) => handleInputChange("timeout", Number(e.target.value))}
          />
          <div className="field-helper">
            The maximum duration of silence allowed before the agent hangs up or prompts the caller.
          </div>
        </div>
      </div>

      {/* SECTION 2: Speech Interruption / Barge-in Behavior & Hold Line */}
      <div className="config-section-simple" style={{ marginBottom: 30 }}>
        <div className="config-group-title" style={{ display: "flex", alignItems: "center", gap: "8px", borderBottom: "1px solid var(--gray-100, #f1f5f9)", paddingBottom: "10px", marginBottom: "18px" }}>
          <VolumeX size={16} style={{ color: "var(--primary, #1e53b8)" }} />
          <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--slate-900)" }}>Barge-In & Background Interruption</span>
        </div>

        {/* Hold Line Slider */}
        <div className="slider-group" style={{ marginBottom: 20 }}>
          <div className="slider-header">
            <span className="field-label">
              Hold Line <span style={{ color: "#94a3b8", fontWeight: "normal", fontSize: "12px", marginLeft: "4px" }}>(Optional)</span>
            </span>
            <span className="slider-val">{selectedAgent?.hold_line ?? 3} sentences</span>
          </div>
          <input
            type="range"
            min={0}
            max={5}
            step={1}
            className="custom-range-slider"
            value={selectedAgent?.hold_line ?? 3}
            onChange={(e) => handleInputChange("hold_line", Number(e.target.value))}
          />
          <div className="field-helper">
            Number of initial sentences where the bot will not ignore any interruptions from the customer.
          </div>
        </div>
      </div>

      {/* SECTION 3: Language Switching Parameters */}
      <div className="config-section-simple" style={{ marginBottom: 30 }}>
        <div className="config-group-title" style={{ display: "flex", alignItems: "center", gap: "8px", borderBottom: "1px solid var(--gray-100, #f1f5f9)", paddingBottom: "10px", marginBottom: "18px" }}>
          <Globe size={16} style={{ color: "var(--primary, #1e53b8)" }} />
          <span style={{ fontSize: "14px", fontWeight: 700, color: "var(--slate-900)" }}>Language Switching</span>
        </div>

        {/* 2-Pair Wrapped Grid Container */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" }}>
          {/* Word Count Range Slider (1 to 5) */}
          <div className="slider-group" style={{ marginBottom: 0 }}>
            <div className="slider-header">
              <span className="field-label">
                Word Count <span style={{ color: "#94a3b8", fontWeight: "normal", fontSize: "12px", marginLeft: "4px" }}>(Optional)</span>
              </span>
              <span className="slider-val">{selectedAgent?.lang_switch_word_count ?? 2} words</span>
            </div>
            <input
              type="range"
              min={1}
              max={5}
              step={1}
              className="custom-range-slider"
              value={selectedAgent?.lang_switch_word_count ?? 2}
              onChange={(e) => handleInputChange("lang_switch_word_count", Number(e.target.value))}
            />
            <div className="field-helper">
              Minimum consecutive words spoken in a new language before switching language mode (range 1 to 5).
            </div>
          </div>

          {/* Percentage Threshold Range Slider (50% to 100%) */}
          <div className="slider-group" style={{ marginBottom: 0 }}>
            <div className="slider-header">
              <span className="field-label">
                Percentage Threshold <span style={{ color: "#94a3b8", fontWeight: "normal", fontSize: "12px", marginLeft: "4px" }}>(Optional)</span>
              </span>
              <span className="slider-val">{selectedAgent?.lang_switch_threshold ?? 75}%</span>
            </div>
            <input
              type="range"
              min={50}
              max={100}
              step={5}
              className="custom-range-slider"
              value={selectedAgent?.lang_switch_threshold ?? 75}
              onChange={(e) => handleInputChange("lang_switch_threshold", Number(e.target.value))}
            />
            <div className="field-helper">
              Confidence percentage threshold required to trigger an automatic language switch (range 50% to 100%).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
